import React, { useState, useEffect } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { ArrowDropUpOutlined } from "@mui/icons-material";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import Premium from "../../../assets/icons/premium.svg";
import { PlannersFilterState } from "../preneed-planners.types";
import { usePlannersFilter } from "../../../common/hooks/plannersUseFilter";
import { useDebounce } from "../../../common/hooks/useDebounce";
import { DualRangeInput } from "../../../common/components/dual-range-input-slider/DualRangeInputSlider";
import "./PlannersFilters.scss";

export type PlannersFiltersProps = {
  initialFilters: PlannersFilterState;
  onChange: (filters: PlannersFilterState) => void;
  sortSource: string;
  setSortSource: any;
};

export default function PlannersFilters({
  initialFilters,
  onChange,
  sortSource,
  setSortSource,
}: PlannersFiltersProps) {
  // CONSTANTS
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // STATES
  const { filters, setFilters, resetFilters } = usePlannersFilter(
    initialFilters,
    onChange
  );

  const [localPriceRange, setLocalPriceRange] = useState<(string | number)[]>([
    initialFilters.priceRange?.[0] ?? "",
    initialFilters.priceRange?.[1] ?? "",
  ]);
  const [localDistanceRange, setLocalDistanceRange] = useState<
    (string | number)[]
  >([
    initialFilters.distanceRange?.[0] ?? "",
    initialFilters.distanceRange?.[1] ?? "",
  ]);

  const debouncedPriceRange = useDebounce(localPriceRange, 1000);
  const debouncedDistanceRange = useDebounce(localDistanceRange, 1000);

  useEffect(() => {
    const min =
      debouncedPriceRange[0] === ""
        ? undefined
        : Number(debouncedPriceRange[0]);
    const max =
      debouncedPriceRange[1] === ""
        ? undefined
        : Number(debouncedPriceRange[1]);

    if (min === undefined && max === undefined) {
      if (filters.priceRange === undefined) {
        return;
      }

      setFilters((prev) => ({ ...prev, priceRange: undefined }));
      return;
    }

    const newMin = min === undefined ? 0 : min;
    const newMax = max === undefined ? 7000 : max;

    const [fixedMin, fixedMax] = clampRange(newMin, newMax);

    if (
      filters.priceRange?.[0] !== fixedMin ||
      filters.priceRange?.[1] !== fixedMax
    ) {
      setFilters((prev) => ({ ...prev, priceRange: [fixedMin, fixedMax] }));
    }
  }, [debouncedPriceRange]);

  useEffect(() => {
    const min =
      debouncedDistanceRange[0] === ""
        ? undefined
        : Number(debouncedDistanceRange[0]);
    const max =
      debouncedDistanceRange[1] === ""
        ? undefined
        : Number(debouncedDistanceRange[1]);

    if (min === undefined && max === undefined) {
      if (filters.distanceRange === undefined) {
        return;
      }

      setFilters((prev) => ({ ...prev, distanceRange: undefined }));
      return;
    }

    const newMin = min === undefined ? 0 : min;
    const newMax = max === undefined ? 150 : max;

    const [fixedMin, fixedMax] = clampRange(newMin, newMax, 150);

    if (
      filters.distanceRange?.[0] !== fixedMin ||
      filters.distanceRange?.[1] !== fixedMax
    ) {
      setFilters((prev) => ({ ...prev, distanceRange: [fixedMin, fixedMax] }));
    }
  }, [debouncedDistanceRange]);

  useEffect(() => {
    setLocalPriceRange([
      filters.priceRange?.[0] ?? "",
      filters.priceRange?.[1] ?? "",
    ]);
    setLocalDistanceRange([
      filters.distanceRange?.[0] ?? "",
      filters.distanceRange?.[1] ?? "",
    ]);
  }, [filters.priceRange, filters.distanceRange]);

  const clampRange = (
    min: number,
    max: number,
    limit = 7000
  ): [number, number] => {
    if (min < 0) min = 0;
    if (max > limit) max = limit;

    if (min >= max) min = max;

    return [min, max];
  };

  return (
    <Box className="planners-filters">
      {/* Filters Header */}
      <Box className="planners-filters__header">
        <Typography variant="h6" className="planners-filters__title">
          Filters
        </Typography>
        <Button
          variant="text"
          onClick={() => {
            resetFilters();
          }}
          className="planners-filters__reset-btn"
        >
          Reset
        </Button>
      </Box>

      {/* ===== PRICE FILTER ===== */}
      <Accordion
        defaultExpanded
        className="planners-filters__accordion accordion-component"
      >
        <AccordionSummary
          expandIcon={
            <ArrowDropUpOutlined
              className="planners-filters__expand-icon"
              style={{ color: "#000" }}
            />
          }
          sx={{
            minHeight: "auto",
            "& .MuiAccordionSummary-content": { m: 0, mt: 1, mb: 1 },
            "& .MuiAccordionSummary-content.Mui-expanded": {
              m: 0,
              mt: 1,
              mb: 1,
            },
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <Typography className="filter-title planners-filters__accordion-title">
            Price
          </Typography>
        </AccordionSummary>

        <AccordionDetails>
          {/* Price Inputs */}
          <DualRangeInput
            label="Price"
            minLimit={0}
            maxLimit={7000}
            step={100}
            value={filters.priceRange}
            onChange={(range) => {
              setFilters((prev) => ({ ...prev, priceRange: range }));
            }}
            unit="$"
          />
        </AccordionDetails>
      </Accordion>

      {/* ===== DISTANCE FILTER ===== */}
      <Accordion
        defaultExpanded
        className="planners-filters__accordion accordion-component"
      >
        <AccordionSummary
          expandIcon={
            <ArrowDropUpOutlined
              className="planners-filters__expand-icon"
              style={{ color: "#000" }}
            />
          }
          sx={{
            minHeight: "auto",
            "& .MuiAccordionSummary-content": { m: 0, mt: 1, mb: 1 },
            "& .MuiAccordionSummary-content.Mui-expanded": {
              m: 0,
              mt: 1,
              mb: 1,
            },
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <Typography className="filter-title planners-filters__accordion-title">
            Distance
          </Typography>
        </AccordionSummary>

        <AccordionDetails>
          {/* Distance Inputs */}
          <DualRangeInput
            label="Distance"
            minLimit={0}
            maxLimit={150}
            step={5}
            value={filters.distanceRange}
            onChange={(range) => {
              setFilters((prev) => ({ ...prev, distanceRange: range }));
            }}
            unit="mi"
          />
        </AccordionDetails>
      </Accordion>

      {/* ===== STAR RATING FILTER ===== */}
      <Accordion
        defaultExpanded
        className="planners-filters__accordion accordion-component"
      >
        <AccordionSummary
          expandIcon={
            <ArrowDropUpOutlined
              className="planners-filters__expand-icon"
              style={{ color: "#000" }}
            />
          }
          sx={{
            minHeight: "auto",
            "& .MuiAccordionSummary-content": { m: 0, mt: 1, mb: 1 },
            "& .MuiAccordionSummary-content.Mui-expanded": {
              m: 0,
              mt: 1,
              mb: 1,
            },
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <Typography className="filter-title planners-filters__accordion-title">
            Star Rating
          </Typography>
        </AccordionSummary>

        <AccordionDetails
          sx={{
            padding: "6px",
            mt: 1,
          }}
        >
          {/** Will enable in future */}
          {/* <Box className="planners-filters__special-buttons">
            <Button
              sx={{
                border: "1px solid #BFC0C2",
                color:
                  filters.sortBy === "rating_high_to_low" &&
                  sortSource === "filters"
                    ? "#fff"
                    : "#4B4F50",
                bgcolor:
                  filters.sortBy === "rating_high_to_low" &&
                  sortSource === "filters"
                    ? "#ebd28d"
                    : "transparent",
                textTransform: "capitalize",
              }}
              endIcon={
                <StarRoundedIcon
                  sx={{
                    color:
                      filters.sortBy === "rating_high_to_low" &&
                      sortSource === "filters"
                        ? "9aa0a8"
                        : "rgba(3, 18, 36, 0.4)",
                  }}
                />
              }
              onClick={() => {
                setSortSource("filters");
                setFilters((prev) => ({
                  ...prev,
                  sortBy:
                    prev.sortBy === "rating_high_to_low"
                      ? undefined
                      : "rating_high_to_low",
                }));
              }}
              className="planners-filters__special-btn planners-filters__special-btn--toprated"
            >
              Top Rated
            </Button>

            <Button
              sx={{
                border: "1px solid #BFC0C2",
                color: "#4B4F50",
                textTransform: "capitalize",
              }}
              endIcon={
                <img
                  src={Premium}
                  alt="Premium Icon"
                  className="planners-filters__premium-icon"
                />
              }
              className="planners-filters__special-btn planners-filters__special-btn--premium"
            >
              Premium
            </Button>
          </Box> */}

          <Box className="planners-filters__rating-buttons">
            {[5, 4, 3].map((star) => {
              const isSelected = filters.ratings?.includes(star);
              return (
                <Button
                  key={star}
                  sx={{
                    border: "1px solid #BFC0C2",
                    color: isSelected ? "#fff" : "#4B4F50",
                    bgcolor: isSelected ? "#ebd28d" : "transparent",
                    padding: "5px 0px !important",
                    margin: "0px !important",
                    minWidth: "48px",
                    flex: 1,
                  }}
                  onClick={() =>
                    setFilters((prev) => {
                      const current = prev.ratings || [];
                      return {
                        ...prev,
                        ratings: isSelected
                          ? current.filter((r) => r !== star)
                          : [...current, star],
                      };
                    })
                  }
                  endIcon={
                    <StarRoundedIcon
                      sx={{
                        color: isSelected ? "" : "#9aa0a8",
                      }}
                    />
                  }
                  className="planners-filters__rating-btn"
                >
                  {star}
                </Button>
              );
            })}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
